import type { Tree, TreeItem, ScenarioState } from "../types";
import IModelData from "../../../types/ModelData";
import FieldsInitializer from "../../ModelDataFields/Input/Initializer";
import apiService from "../../../services/ApiService";
import { useEditorStore } from "../../../shared/stores/useEditorStore";

export const removeElem = (itemId: string, tree: Tree): void => {
  if (!tree.items[itemId]) return;
  if (tree.items[itemId].childrens) {
    tree.items[itemId].childrens.forEach((id) => {
      removeElem(id, tree);
    });
  }
  delete tree.items[itemId];
};

export const mapModelData = (modelData: IModelData): TreeItem => {
  return {
    id: modelData.id.toString(),
    name: modelData.name,
    type: modelData.type,
    kind: modelData.kind,
    position: modelData.position,
    childrens: modelData.childrens.map((e) => e.toString()),
    possibleChildrens: modelData.possible_childrens,
    childrensDefinition: modelData.childrens_definition,
    parentId: modelData.parent_id ? modelData.parent_id.toString() : null,
    fields: FieldsInitializer(modelData.fields),
    has_related_output: modelData.has_related_output,
    relatedModelDataId: modelData.related_model_id !== null
      ? modelData.related_model_id.toString()
      : null,
    documentation: modelData.documentation,
    isInconsiderable: modelData.is_inconsiderable,
    isDeletable: modelData.is_deletable,
    expanded: false,
    editing: false,
    anchorEl: null,
    sensitivityModelData: null,
    deleteModalOpen: false,
    detailsModalOpen: false,
  };
};

export const removeElemFromTree = (
  itemId: string,
  currentState: ScenarioState,
  reloadState: () => void,
  _recreateExcelInputIfSync?: () => void,
  patchTree?: (updater: (prev: ScenarioState) => ScenarioState) => void,
  onError?: (error: unknown) => void,
  // Fires after a successful delete on both the patchTree and legacy paths.
  // `reloadState` only fires on the legacy path (by design — patchTree already
  // refreshes the cache), so callers that need a completion signal independent
  // of the code path should use this instead.
  onDone?: () => void,
): void => {
  // Capture the info we need from the current snapshot before any async work.
  const tree = currentState["Input"].tree;
  const deletedItem = tree.items[itemId];
  if (!deletedItem) return;
  const parentId = deletedItem.parentId!;
  const relatedOutputId = deletedItem.relatedModelDataId;

  apiService.deleteModelData(parseInt(itemId)).then(async (response) => {
    if (!response || !response.ok) {
      onError?.(new Error(`deleteModelData failed for ${itemId}`));
      return;
    }
    const parentResponse = await apiService.getModelData(parseInt(parentId));
    if (!parentResponse || !parentResponse.ok) {
      onError?.(new Error(`getModelData failed for parent ${parentId}`));
      return;
    }
    const updatedParent = mapModelData(await parentResponse.json());

    useEditorStore.getState().setNodeExpanded(updatedParent.kind + parentId, true);

    // Apply all mutations atomically via patchTree so that concurrent
    // WebSocket events cannot create a divergent items object.
    if (patchTree) {
      patchTree((prev) => {
        const newInputItems = { ...prev.Input.tree.items };
        const newOutputItems = { ...prev.Output.tree.items };

        // Replace parent with refreshed version from API
        newInputItems[parentId] = updatedParent;

        // Remove deleted item and its descendants from input
        const removeFromItems = (id: string, items: Record<string, any>) => {
          const item = items[id];
          if (!item) return;
          if (item.childrens) {
            item.childrens.forEach((childId: string) => removeFromItems(childId, items));
          }
          delete items[id];
        };
        removeFromItems(itemId, newInputItems);

        // Remove related output item and update output parent
        if (relatedOutputId) {
          const outputItem = newOutputItems[relatedOutputId];
          if (outputItem) {
            const outputParent = outputItem.parentId ? newOutputItems[outputItem.parentId] : null;
            if (outputParent) {
              newOutputItems[outputParent.id] = {
                ...outputParent,
                childrens: outputParent.childrens.filter((id: string) => id !== outputItem.id.toString()),
              };
            }
            removeFromItems(relatedOutputId, newOutputItems);
          }
        }

        return {
          ...prev,
          isSync: false,
          Input: { ...prev.Input, tree: { ...prev.Input.tree, items: newInputItems } },
          Output: { ...prev.Output, tree: { ...prev.Output.tree, items: newOutputItems } },
        };
      });
    } else {
      // Legacy fallback: mutate in place and trigger reload
      tree.items[parentId] = updatedParent;
      if (relatedOutputId) {
        const outputTree = currentState["Output"].tree;
        const outputItem = outputTree.items[relatedOutputId];
        if (outputItem) {
          removeElem(relatedOutputId, outputTree);
          const parentOutputItem = outputTree.items[outputItem.parentId!];
          if (parentOutputItem) {
            const idx = parentOutputItem.childrens.indexOf(outputItem.id.toString());
            if (idx !== -1) parentOutputItem.childrens.splice(idx, 1);
          }
        }
      }
      removeElem(itemId, tree);
      currentState.isSync = false;
      reloadState();
    }
    onDone?.();
  }).catch((err) => onError?.(err));
};

export const getBrothersNames = (item: TreeItem, tree: Tree): string[] => {
  const parent = tree.items[item.parentId!];
  if (!parent) return [];
  return parent.childrens
    .map((id) => tree.items[id]?.name)
    .filter(Boolean);
};

export const getItemStyle = (
  _isDragging: boolean,
  draggableStyle: React.CSSProperties | undefined,
): React.CSSProperties => ({
  userSelect: "none" as const,
  padding: 2,
  ...draggableStyle,
});
